import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { dayMonth, periodError, wholeMonthsRange, type ProfitPeriod, type ProfitPeriodKind } from './profitabilityModel';
import DateInput from '../ui/DateInput';

/**
 * P-28b (D-169) - the period of a profitability screen: "מההתחלה" (projects only),
 * a month, or a date range. One quiet segmented control and, under it, the field
 * the choice needs. Revenue is booked per month, so a range says so once.
 */
export default function ProfitPeriodPicker({ value, onChange, allowAll }: {
  value: ProfitPeriod;
  onChange: (next: ProfitPeriod) => void;
  /** A project can be seen "from the start"; a customer cannot. */
  allowAll: boolean;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const kinds: ProfitPeriodKind[] = allowAll ? ['all', 'month', 'range'] : ['month', 'range'];
  const error = periodError(value);
  const whole = wholeMonthsRange(value);

  return (
    <div className="space-y-2" data-profit-period="">
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label={t('profitability.period.label')} className="inline-flex rounded-md border border-surface-200 p-0.5">
          {kinds.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={value.kind === k}
              onClick={() => onChange({ ...value, kind: k })}
              className={`rounded px-3 py-1 text-sm ${value.kind === k ? 'bg-surface-subtle font-medium text-text' : 'text-text-2'}`}
            >
              {t(`profitability.period.${k}`)}
            </button>
          ))}
        </div>
        {value.kind === 'month' && (
          <div className="flex items-center gap-2">
            <label htmlFor={`${uid}-m`} className="sr-only">{t('profitability.period.month')}</label>
            <input
              id={`${uid}-m`}
              type="month"
              className="input w-auto"
              value={value.month}
              onChange={(e) => onChange({ ...value, month: e.target.value })}
              aria-invalid={error === 'month' || undefined}
            />
          </div>
        )}
        {value.kind === 'range' && (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`${uid}-f`} className="text-sm text-text-2">{t('profitability.period.from')}</label>
            <DateInput
              id={`${uid}-f`}
              className="input w-auto"
              value={value.from}
              max={value.to || undefined}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
              aria-invalid={error === 'order' || undefined}
            />
            <label htmlFor={`${uid}-t`} className="text-sm text-text-2">{t('profitability.period.to')}</label>
            <DateInput
              id={`${uid}-t`}
              className="input w-auto"
              value={value.to}
              min={value.from || undefined}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
              aria-invalid={error === 'order' || undefined}
            />
          </div>
        )}
      </div>
      {error === 'order' && <p role="alert" className="text-sm text-danger-700">{t('profitability.period.orderError')}</p>}
      {error === 'range' && value.kind === 'range' && <p className="text-xs text-text-2">{t('profitability.period.rangeHint')}</p>}
      {whole && (
        <p className="text-xs text-text-2" data-whole-months="">
          {t('profitability.period.wholeMonths', { from: dayMonth(whole.from) || '…', to: dayMonth(whole.to) || '…' })}
        </p>
      )}
      {value.kind === 'range' && !error && !whole && <p className="text-xs text-text-2">{t('profitability.period.rangeNote')}</p>}
    </div>
  );
}
